/**
 * Lot Interaction Plugin for Native Interactive Site Map (ISM)
 *
 * Sole owner of lot polygon coloring and interaction: initial status colors, click/hover
 * selection, and chip-driven filter coloring (matched = status color, unmatched = grey).
 *
 * Deliberately separate from `renderCommunitySiteMap.plugin.js` (which only creates/attaches
 * the SVG overlay). This plugin reacts to `context.services.svgOverlayReady$` — emitted once
 * the overlay's SVG element exists — and to Redux store changes (selected lot, active chip).
 *
 * @module nativeInteractiveSiteMap/plugins/lotInteraction.plugin
 */
import { map, distinctUntilChanged, pairwise } from 'rxjs/operators';
import createLotPolygonColors from '../components/lotPolygonColors.dom';
import createLotPolygonInteractionHandlers, {
  LOT_INTERACTIVE_LAYERS_SELECTOR,
} from '../handlers/lotPolygonInteractionHandlers';
import { selectSelectedLotId, selectLotStatusMap, selectMatchedLotIds } from '../redux/selectors';
import createMapBackgroundClickedEpic from '../redux/mapBackgroundClickedEpic';
import registerEpicOnce from '../redux/registerEpicOnce';

export default function createLotInteractionPlugin() {
  const state = {
    lotColors: null,
    overlay: null,
    svgOverlaySubscription: null,
    filterSubscription: null,
    selectedLotSubscription: null,
  };

  /**
   * Re-applies lot coloring whenever the active chip/matched lots change. Set up once per
   * svgOverlayReady$ emission (needs state.lotColors, which is bound to that svgElement) and
   * torn down in stop().
   * @returns {import('rxjs').Subscription}
   */
  function setupSubscriptions(context) {
    function onMatchedLotIdsChanged(matchedSitePlanLotIds) {
      const currentState = context.services.getState();
      state.lotColors?.applyFilter(selectLotStatusMap(currentState), matchedSitePlanLotIds);
    }

    return context.services.store$.pipe(
      map((storeState) => selectMatchedLotIds(storeState).sitePlanLotIds),
      distinctUntilChanged(),
    ).subscribe(onMatchedLotIdsChanged);
  }

  function onSelectedLotChanged([previousSelectedLotId, selectedLotId]) {
    state.lotColors?.setSelected(previousSelectedLotId, selectedLotId);
  }

  /**
   * Keeps the --selected CSS class in sync with selectedLotId, whatever dispatched the change
   * (lotSelected on click, navigateBack, chipSelected, viewReset, ...). Single source of truth —
   * callers only need to dispatch the right action; they never need to remember to also call
   * lotColors.setSelected themselves.
   *
   * pairwise() never emits on the very first store$ value after start() (it needs two values to
   * form a pair) — safe here because viewReset guarantees selectedLotId is back to null every
   * time this plugin is freshly mounted, so there is nothing to sync on that first, un-paired
   * value (same reasoning lotMapSelection.plugin.js documents for its own pairwise() use).
   *
   * @returns {import('rxjs').Subscription}
   */
  function setupSelectedLotSubscription(context) {
    return context.services.store$.pipe(
      map((storeState) => selectSelectedLotId(storeState)),
      distinctUntilChanged(),
      pairwise(),
    ).subscribe(onSelectedLotChanged);
  }

  return {
    name: 'lot-interaction',

    async init(context) {
      const mapBackgroundClickedEpic = createMapBackgroundClickedEpic({
        dataActions: context.services.dataActions,
      });
      registerEpicOnce(context.services.epic$, 'map-background-clicked', mapBackgroundClickedEpic);
    },

    async start(context) {
      function onSvgOverlayReady({ overlay, svgElement }) {
        state.overlay = overlay;

        // Grey (unmatched or NotFound) lots never reach this — the --unmatched rule in
        // _components.native-interactive-site-map.scss sets pointer-events: none, which keeps
        // the click from ever resolving to their SVG element in the first place (see
        // svgLotInteraction.js's findLotElement, which only ever sees event.target for elements
        // the browser actually hit-tests). lotPolygonColors.dom.js only toggles the class —
        // it isn't where the pointer-events behavior itself is defined.
        function onLotSelected(lotId) {
          context.services.dispatch(context.services.dataActions.lotSelected(lotId));
        }

        function onLotHovered(lotId) {
          state.lotColors?.setHoveredMany([lotId]);
        }

        function onLotLeft() {
          state.lotColors?.clearHovered();
        }

        // Clicking anywhere on the map outside a lot is how the user leaves a sub-view
        // (e.g. lotMapSelection) and returns to listing — by product decision, there is no
        // back/close button in those views' headers. Dispatches a marker action rather than
        // navigateBack() directly: mapBackgroundClickedEpic (registered in init() below) decides
        // whether to unwind viewHistory or just clear the current selection, so this is always
        // safe to dispatch regardless of which view is currently active or whether a lot is
        // selected without having navigated anywhere (e.g. a selected Coming Soon lot).
        function onMapBackgroundClicked() {
          context.services.dispatch(context.services.dataActions.mapBackgroundClicked());
        }

        const handlers = createLotPolygonInteractionHandlers({
          onLotSelected, onLotHovered, onLotLeft, onMapBackgroundClicked,
        });
        handlers.onSvgReady(svgElement);

        state.lotColors = createLotPolygonColors(svgElement);

        // Card hover (Floor Plan / QMI cards) drives the same
        // lotColors instance as the native map hover above — one instance, one source of truth
        // for "what's currently hovered", so the two can never desync.
        state.cardHoverSubscription?.unsubscribe?.();
        state.cardHoverSubscription = context.services.cardLotHovered$.subscribe((sitePlanLotIds) => {
          if (sitePlanLotIds && sitePlanLotIds.size > 0) {
            state.lotColors?.setHoveredMany(sitePlanLotIds);
          } else {
            state.lotColors?.clearHovered();
          }
        });

        const initialState = context.services.getState();
        state.lotColors.applyFilter(selectLotStatusMap(initialState), selectMatchedLotIds(initialState).sitePlanLotIds);

        overlay.bindEvents({
          onLotClick: ({ lotId }) => handlers.onLotClick({ lotId }),
          onLotHover: ({ lotId }) => handlers.onLotHover({ lotId }),
          onLotEnter: (args) => handlers.onLotEnter(args),
          onLotLeave: (args) => handlers.onLotLeave(args),
          onBackgroundClick: () => handlers.onBackgroundClick(),
          boundarySelector: LOT_INTERACTIVE_LAYERS_SELECTOR,
        });

        state.filterSubscription?.unsubscribe?.();
        state.filterSubscription = setupSubscriptions(context);

        state.selectedLotSubscription?.unsubscribe?.();
        state.selectedLotSubscription = setupSelectedLotSubscription(context);
      }

      state.svgOverlaySubscription = context.services.svgOverlayReady$.subscribe(onSvgOverlayReady);
    },

    async stop() {
      state.svgOverlaySubscription?.unsubscribe?.();
      state.svgOverlaySubscription = null;

      state.filterSubscription?.unsubscribe?.();
      state.filterSubscription = null;

      state.selectedLotSubscription?.unsubscribe?.();
      state.selectedLotSubscription = null;

      state.cardHoverSubscription?.unsubscribe?.();
      state.cardHoverSubscription = null;

      // Unbind our own DOM listeners now, instead of relying on renderCommunitySiteMap.plugin
      // detaching the overlay later (pluginHost stops plugins in reverse registration order,
      // so this plugin stops before that one — see PR #6518 review).
      state.overlay?.bindEvents();
      state.overlay = null;

      state.lotColors = null;
    },
  };
}
